<div class="grid-title-event">
    <h2 id="commission-rules-title">Commission Rules</h2>

    <div class="events-container">
        <button color="accent" id="run-evaluation-button" aria-label="Run evaluation" mat-stroked-button
                (click)="handleRunEvaluation()" [disabled]="isEvaluating">
            @if (isEvaluating) {
                <mat-spinner diameter="18" class="inline-spinner"></mat-spinner>
                Evaluating... ({{evalTotalEvaluated}} checked, {{evalTotalViolations}} violations, {{evalTotalRemaining}} remaining)
            } @else {
                <mat-icon fontIcon="play_circle_outline"></mat-icon>
                Run Evaluation
            }
        </button>
        <button color="warn" id="run-fixes-button" aria-label="Apply fixes" mat-stroked-button
                (click)="handleRunFixes()" [disabled]="isFixing" class="ml-8">
            <mat-icon fontIcon="build"></mat-icon>
            {{ isFixing ? 'Applying...' : 'Apply Fixes' }}
        </button>
        @if (isDirector) {
            <button color="warn" id="force-apply-fixes-button" aria-label="Force apply fixes" mat-stroked-button
                    (click)="handleForceApplyFixes()" [disabled]="isForceFixing" class="ml-8">
                @if (isForceFixing) {
                    <mat-spinner diameter="18" class="inline-spinner"></mat-spinner>
                    Force Applying... ({{forceFixTotalFixed}} fixed, {{forceFixTotalRemaining}} remaining)
                } @else {
                    <mat-icon fontIcon="warning"></mat-icon>
                    Force Apply Fixes
                }
            </button>
        }
        <button color="primary" id="new-commission-rule-button" aria-label="New commission rule" mat-stroked-button
                (click)="handleOpenRule()" class="ml-8">
            <mat-icon fontIcon="add"></mat-icon>
            New Rule
        </button>
    </div>
</div>

<mat-tab-group dynamicHeight (selectedIndexChange)="handleTabChange($event)">
    <mat-tab label="Rules">
        <mat-table id="commission-rules-table" [dataSource]="items" matSort (matSortChange)="handleSortChange($event)">
            <ng-container matColumnDef="name">
                <mat-header-cell class="mat-header-cell" *matHeaderCellDef mat-sort-header="name">
                    Rule Name
                </mat-header-cell>
                <mat-cell *matCellDef="let element">
                    {{element?.name}}
                </mat-cell>
            </ng-container>

            <ng-container matColumnDef="criteria">
                <mat-header-cell class="mat-header-cell" *matHeaderCellDef>
                    Criteria
                </mat-header-cell>
                <mat-cell *matCellDef="let element">
                    {{element?.criteria}}
                </mat-cell>
            </ng-container>

            <ng-container matColumnDef="priority">
                <mat-header-cell class="mat-header-cell" *matHeaderCellDef mat-sort-header="priority">
                    Priority
                </mat-header-cell>
                <mat-cell *matCellDef="let element">
                    {{element?.priority}}
                </mat-cell>
            </ng-container>

            <ng-container matColumnDef="enabled">
                <mat-header-cell class="mat-header-cell" *matHeaderCellDef>
                    Enabled
                </mat-header-cell>
                <mat-cell *matCellDef="let element">
                    <mat-slide-toggle [(ngModel)]="element.isEnabled"
                                      (change)="handleToggleEnabled(element)"
                                      [disabled]="isTogglePending">
                        {{element?.isEnabled ? 'ON' : 'OFF'}}
                    </mat-slide-toggle>
                </mat-cell>
            </ng-container>

            <ng-container matColumnDef="paused">
                <mat-header-cell class="mat-header-cell" *matHeaderCellDef>
                    Paused
                </mat-header-cell>
                <mat-cell *matCellDef="let element">
                    <mat-slide-toggle [(ngModel)]="element.isPaused"
                                      (change)="handleTogglePaused(element)"
                                      [disabled]="isTogglePending">
                        {{element?.isPaused ? 'PAUSED' : 'ACTIVE'}}
                    </mat-slide-toggle>
                </mat-cell>
            </ng-container>

            <ng-container matColumnDef="autoFixDelay">
                <mat-header-cell class="mat-header-cell" *matHeaderCellDef>
                    Auto-Fix Delay
                </mat-header-cell>
                <mat-cell *matCellDef="let element">
                    {{element?.autoFixDelayMinutes}} min
                </mat-cell>
            </ng-container>

            <ng-container matColumnDef="action">
                <mat-header-cell class="mat-header-cell" *matHeaderCellDef>
                    Action
                </mat-header-cell>
                <mat-cell *matCellDef="let element">
                    <button mat-icon-button aria-label="evaluate rule" matTooltip="Evaluate this rule"
                            (click)="handleRunEvaluation(element?.workflowId)" [disabled]="isBusy">
                        <mat-icon fontIcon="play_circle_outline"/>
                    </button>
                    <button mat-icon-button aria-label="apply fixes for rule" matTooltip="Apply fixes for this rule"
                            (click)="handleRunFixes(element?.workflowId)" [disabled]="isBusy">
                        <mat-icon fontIcon="build"/>
                    </button>
                    @if (isDirector) {
                        <button mat-icon-button aria-label="force apply fixes for rule" matTooltip="Force apply fixes for this rule"
                                (click)="handleForceApplyFixes(element?.workflowId)" [disabled]="isBusy">
                            <mat-icon fontIcon="warning"/>
                        </button>
                    }
                    <button mat-icon-button aria-label="edit rule" (click)="handleOpenRule(element?.workflowId)">
                        <mat-icon fontIcon="edit"/>
                    </button>
                    <button mat-icon-button aria-label="delete rule" (click)="handleDeleteRule(element)">
                        <mat-icon fontIcon="delete"/>
                    </button>
                </mat-cell>
            </ng-container>

            <mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"/>
            <mat-row *matRowDef="let row; columns: displayedColumns"/>
        </mat-table>
        <mat-paginator [length]="page?.totalItemCount" [hidePageSize]="true"
                       [pageSize]="page?.pageSize" [pageIndex]="page?.pageIndex"
                       (page)="handlePageChanged($event)">
        </mat-paginator>
    </mat-tab>

    <mat-tab label="Evaluations">
        <div class="evaluation-filters">
            <mat-form-field appearance="outline" class="date-filter">
                <mat-label>Date From</mat-label>
                <input matInput [matDatepicker]="dateFromPicker"
                       [(ngModel)]="evaluationDateFrom"
                       (dateChange)="handleEvaluationDateChange()">
                <mat-datepicker-toggle matIconSuffix [for]="dateFromPicker"></mat-datepicker-toggle>
                <mat-datepicker #dateFromPicker></mat-datepicker>
            </mat-form-field>
            <mat-form-field appearance="outline" class="date-filter">
                <mat-label>Date To</mat-label>
                <input matInput [matDatepicker]="dateToPicker"
                       [(ngModel)]="evaluationDateTo"
                       (dateChange)="handleEvaluationDateChange()">
                <mat-datepicker-toggle matIconSuffix [for]="dateToPicker"></mat-datepicker-toggle>
                <mat-datepicker #dateToPicker></mat-datepicker>
            </mat-form-field>
            <mat-form-field appearance="outline" class="status-filter">
                <mat-label>Status</mat-label>
                <mat-select [value]="evaluationStatusFilter"
                            (selectionChange)="handleEvaluationStatusFilter($event.value)">
                    <mat-option value="">All</mat-option>
                    <mat-option value="Violation">Violation</mat-option>
                    <mat-option value="Fixed">Fixed</mat-option>
                    <mat-option value="Ignored">Ignored</mat-option>
                </mat-select>
            </mat-form-field>
        </div>

        <mat-table id="commission-evaluations-table" [dataSource]="evaluationItems">
            <ng-container matColumnDef="workflowName">
                <mat-header-cell class="mat-header-cell" *matHeaderCellDef>
                    Workflow
                </mat-header-cell>
                <mat-cell *matCellDef="let element">
                    {{element?.workflowName}}
                </mat-cell>
            </ng-container>

            <ng-container matColumnDef="ruleName">
                <mat-header-cell class="mat-header-cell" *matHeaderCellDef>
                    Rule
                </mat-header-cell>
                <mat-cell *matCellDef="let element">
                    {{element?.ruleName}}
                </mat-cell>
            </ng-container>

            <ng-container matColumnDef="adxBookingId">
                <mat-header-cell class="mat-header-cell" *matHeaderCellDef>
                    Booking ID
                </mat-header-cell>
                <mat-cell *matCellDef="let element">
                    {{element?.adxBookingId}}
                </mat-cell>
            </ng-container>

            <ng-container matColumnDef="adxAgentBookingId">
                <mat-header-cell class="mat-header-cell" *matHeaderCellDef>
                    Agent Booking ID
                </mat-header-cell>
                <mat-cell *matCellDef="let element">
                    {{element?.adxAgentBookingId}}
                </mat-cell>
            </ng-container>

            <ng-container matColumnDef="status">
                <mat-header-cell class="mat-header-cell" *matHeaderCellDef>
                    Status
                </mat-header-cell>
                <mat-cell *matCellDef="let element">
                    <span [class]="'status-' + element?.status?.toLowerCase()">
                        {{element?.status}}
                    </span>
                </mat-cell>
            </ng-container>

            <ng-container matColumnDef="actual">
                <mat-header-cell class="mat-header-cell" *matHeaderCellDef>
                    Actual
                </mat-header-cell>
                <mat-cell *matCellDef="let element" class="wrap-cell">
                    @for (ev of element?.expectedValues; track ev?.id) {
                        <div class="cell-value-row">
                            <strong>{{ev?.fieldName}}:</strong> {{ev?.actualValue ?? '—'}}
                        </div>
                    }
                </mat-cell>
            </ng-container>

            <ng-container matColumnDef="expected">
                <mat-header-cell class="mat-header-cell" *matHeaderCellDef>
                    Expected
                </mat-header-cell>
                <mat-cell *matCellDef="let element" class="wrap-cell">
                    @for (ev of element?.expectedValues; track ev?.id) {
                        <div class="cell-value-row">
                            <strong>{{ev?.fieldName}}:</strong> {{ev?.expectedValue ?? '—'}}
                        </div>
                    }
                </mat-cell>
            </ng-container>

            <ng-container matColumnDef="isFixed">
                <mat-header-cell class="mat-header-cell" *matHeaderCellDef>
                    Fixed
                </mat-header-cell>
                <mat-cell *matCellDef="let element">
                    @if (element?.isFixed) {
                        <mat-icon fontIcon="check_circle" class="fixed-icon"></mat-icon>
                        <span class="fixed-date">{{element?.fixedAt | date:'short'}}</span>
                    } @else {
                        <mat-icon fontIcon="cancel" class="not-fixed-icon"></mat-icon>
                    }
                </mat-cell>
            </ng-container>

            <ng-container matColumnDef="dateCreated">
                <mat-header-cell class="mat-header-cell" *matHeaderCellDef>
                    Created
                </mat-header-cell>
                <mat-cell *matCellDef="let element">
                    {{element?.dateCreated | date:'short'}}
                </mat-cell>
            </ng-container>

            <ng-container matColumnDef="evaluationAction">
                <mat-header-cell class="mat-header-cell" *matHeaderCellDef>
                    Action
                </mat-header-cell>
                <mat-cell *matCellDef="let element">
                    @if (element?.status === 'Violation') {
                        <button mat-icon-button aria-label="ignore evaluation"
                                matTooltip="Ignore — exclude from auto-fix"
                                (click)="handleUpdateEvaluationStatus(element, 'Ignored')">
                            <mat-icon fontIcon="block"></mat-icon>
                        </button>
                    }
                    @if (element?.status === 'Ignored') {
                        <button mat-icon-button aria-label="restore to violation"
                                matTooltip="Restore to Violation"
                                (click)="handleUpdateEvaluationStatus(element, 'Violation')">
                            <mat-icon fontIcon="undo"></mat-icon>
                        </button>
                    }
                </mat-cell>
            </ng-container>

            <mat-header-row *matHeaderRowDef="evaluationColumns; sticky: true"/>
            <mat-row *matRowDef="let row; columns: evaluationColumns"/>
        </mat-table>
        <mat-paginator [length]="evaluationPage?.totalItemCount" [hidePageSize]="true"
                       [pageSize]="evaluationPage?.pageSize" [pageIndex]="evaluationPage?.pageIndex"
                       (page)="handleEvaluationPageChanged($event)">
        </mat-paginator>
    </mat-tab>
</mat-tab-group>

@if (isBusy) {
    <div class="busy-overlay">
        <mat-spinner diameter="64"></mat-spinner>
    </div>
}
